import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, TrendingUp, TrendingDown, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { WatchAdd } from "@/components/WatchAdd";

const TABS = [["1", "First 1 min"], ["3", "First 3 min"], ["5", "First 5 min"], ["15", "First 15 min"]];
const copyTv = async (rows, label) => {
  if (!rows.length) return toast.error("No stocks to copy");
  const text = rows.map((r) => `NSE:${r.symbol.replace(/[&-]/g, "_")}`).join(",");
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${rows.length} ${label} stocks — paste into a TradingView watchlist`);
  } catch {
    toast.error("Copy failed — clipboard blocked");
  }
};
const fmtV = (v) => (v >= 1e5 ? `${(v / 1e5).toFixed(2)}L` : v?.toLocaleString("en-IN"));

const Row = ({ r, onOpen }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-200 text-xs font-mono hover:bg-slate-100 cursor-pointer"
    onClick={() => onOpen(r.instrument_id)} data-testid={`oadr-row-${r.symbol}`}>
    <span className="w-36 shrink-0 font-semibold text-slate-900">{r.symbol}
      <span className={`ml-1 text-[9px] px-1 font-bold ${r.tier === 5 ? "bg-amber-400 text-black" : "bg-amber-500/20 text-amber-300 border border-amber-500/50"}`}
        data-testid={`oadr-x-${r.symbol}`}>{r.x}×</span>
      <span className="block text-[9px] text-slate-500 font-normal">{r.sector}</span>
    </span>
    <span className="w-24 text-right text-slate-700" title={`Average of the same opening window over ${r.days} past sessions: ${fmtV(r.avg)}`}>{fmtV(r.v)}</span>
    <span className="w-20 text-right text-slate-500" title="Range Matrix level broken">{r.level}</span>
    <span className="w-20 text-right text-slate-800" title="Opening candle close">{r.c?.toFixed(2)}</span>
    <span className={`w-16 text-right ${r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.change_pct == null ? "—" : `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%`}</span>
    <span className="ml-auto" onClick={(e) => e.stopPropagation()}><WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} className="h-6 w-6" /></span>
  </div>
);

const Side = ({ dir, rows, onOpen }) => (
  <div className="border border-slate-200 bg-white" data-testid={`oadr-list-${dir}`}>
    <div className={`flex items-center gap-1.5 px-2 h-8 text-xs font-bold font-mono border-b border-slate-200 ${dir === "up" ? "text-emerald-600" : "text-rose-600"}`}>
      {dir === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {dir === "up" ? "Range Matrix Breakout · Bullish" : "Range Matrix Breakdown · Bearish"}<span className="text-slate-500 font-normal">{rows.length}</span>
    </div>
    <div className="flex gap-2 px-2 py-1 text-[9px] uppercase text-slate-500 font-mono border-b border-slate-200">
      <span className="w-36">Stock · vol ×</span><span className="w-24 text-right">Volume</span><span className="w-20 text-right">RM level</span>
      <span className="w-20 text-right">Close</span><span className="w-16 text-right">Day %</span>
    </div>
    {rows.length ? rows.map((r) => <Row key={r.symbol} r={r} onOpen={onOpen} />)
      : <p className="p-4 text-xs font-mono text-slate-500" data-testid={`oadr-empty-${dir}`}>No stock qualified</p>}
  </div>
);

export default function OpeningAdr() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("1");
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => api.get("/today/opening-adr").then((r) => setD(r.data)).catch(() => setD({ tabs: {} }));
    load();
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, []);
  const t = d?.tabs?.[tab] || { up: [], down: [] };
  const open = (id) => navigate(`/app/instrument/${id}`);
  return (
    <div className="space-y-3" data-testid="opening-adr-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Zap className="h-5 w-5 text-amber-400" /> Opening Thrust</h1>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">opening candle closes beyond the Range Matrix with volume 3× or more its usual for the same window (avg of up to 20 past sessions) · gold = 5×+ · session {d?.session || "—"} · lists build at 09:31</p>
      </div>
      <div className="flex flex-wrap items-center gap-1" data-testid="oadr-tabs">
        {TABS.map(([k, label]) => {
          const n = (d?.tabs?.[k]?.up?.length || 0) + (d?.tabs?.[k]?.down?.length || 0);
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`oadr-tab-${k}`}
              className={`h-8 px-3 text-xs font-mono border transition-colors ${tab === k ? "border-blue-500 bg-blue-500/15 text-slate-900" : "border-slate-300 text-slate-500 hover:text-slate-900"}`}>
              {label} <span className="text-slate-500">{d ? n : ""}</span>
            </button>
          );
        })}
        <button onClick={() => copyTv([...t.up, ...t.down], TABS.find(([k]) => k === tab)[1].toLowerCase())} data-testid="oadr-copy-tv"
          className="ml-auto h-8 px-3 text-xs font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5">
          <Copy className="h-3.5 w-3.5 text-blue-600" /> Copy to TradingView
        </button>
      </div>
      {!d ? <p className="p-6 text-xs font-mono text-slate-500">Loading…</p> : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid={`oadr-panel-${tab}`}>
          <Side dir="up" rows={t.up} onOpen={open} />
          <Side dir="down" rows={t.down} onOpen={open} />
        </div>
      )}
    </div>
  );
}
